import { deriveCanonicalProcessMetrics, groupCanonicalEventsByCase } from './processMetrics.js';
import { deriveCanonicalLifecycleSummary } from '../lifecycle.js';
import { deriveCanonicalConformanceSummary } from '../conformance.js';

function populationStdDev(values) {
  if (!values.length) return 0;
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  const variance = values.reduce((sum, value) => sum + ((value - mean) ** 2), 0) / values.length;
  return Math.sqrt(variance);
}

function toMillis(value) {
  if (!value) return NaN;
  const raw = String(value).trim().replace(' ', 'T');
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? ms : NaN;
}

function caseCycleTimeSeconds(c, caseEvents) {
  const start = toMillis(c?.createdAt || caseEvents?.[0]?.timestamp);
  const finish = toMillis(c?.closedAt || caseEvents?.[caseEvents.length - 1]?.timestamp);
  if (!Number.isFinite(start) || !Number.isFinite(finish) || finish < start) return 0;
  return (finish - start) / 1000;
}

/**
 * Evidence/summary metrics computed directly from the canonical process model.
 *
 * The returned shape intentionally mirrors the legacy Process Advisor total
 * metrics row so Evidence/reporting can migrate without changing every consumer
 * at once. Metrics that still require legacy lifecycle classification, workflow
 * configuration or rule evaluation are returned as null and are explicitly
 * described in metricProvenance.
 */
export function deriveCanonicalEvidenceMetrics(dataset) {
  const base = deriveCanonicalProcessMetrics(dataset);
  const cases = dataset?.cases || [];
  const grouped = groupCanonicalEventsByCase(dataset);
  const lifecycle = deriveCanonicalLifecycleSummary(dataset);
  const conformance = deriveCanonicalConformanceSummary(dataset);

  const cycleTimes = [];
  let totalTransitions = 0;
  let reworkedCases = 0;
  let totalCustomerComments = 0;
  let totalInternalComments = 0;
  let totalWorklogs = 0;
  let totalLoggedSeconds = 0;
  const stepKeys = new Set();

  for (const c of cases) {
    const events = grouped.get(String(c.id)) || [];
    const cycle = caseCycleTimeSeconds(c, events);
    if (cycle > 0) cycleTimes.push(cycle);

    totalTransitions += Math.max(events.length - 1, 0);
    const seen = new Set();
    let repeated = false;
    for (let i = 0; i < events.length; i++) {
      const activity = String(events[i]?.activity || '');
      if (activity && seen.has(activity)) repeated = true;
      if (activity) seen.add(activity);
      if (i > 0) {
        const from = String(events[i - 1]?.activity || '');
        const to = activity;
        if (from || to) stepKeys.add(`${from}\u0000${to}`);
      }
    }
    if (repeated) reworkedCases += 1;

    const engagement = c?.extensions?.engagement || {};
    totalCustomerComments += Number(engagement.publicCommentCount || engagement.customerCommentCount || 0);
    totalInternalComments += Number(engagement.internalCommentCount || 0);
    totalWorklogs += Number(engagement.worklogCount || 0);
    totalLoggedSeconds += Number(engagement.totalLoggedSeconds || 0);
  }

  const count = cases.length;
  const average = (value) => count > 0 ? value / count : 0;
  const cycleTime = cycleTimes.length
    ? cycleTimes.reduce((sum, value) => sum + value, 0) / cycleTimes.length
    : 0;

  return {
    period: 'Model Total',
    period_type: 'total',
    start_date: base.earliestTimestamp ? String(base.earliestTimestamp).slice(0, 10) : '',
    end_date: base.latestTimestamp ? String(base.latestTimestamp).slice(0, 10) : '',

    // Work item / process metrics available natively from the canonical model.
    work_items: base.caseCount,
    events: base.eventCount,
    variants: base.variantCount,
    activities: base.activityCount,
    steps: stepKeys.size,
    resources: base.resourceCount,
    cycle_time: cycleTime,
    median_cycle_time: base.medianCycleTimeSeconds,
    std_deviation: populationStdDev(cycleTimes),
    avg_transitions: average(totalTransitions),
    rework_rate: average(reworkedCases),

    // Collaboration/effort extensions are canonical as of Sprint 1 Increment 3.
    avg_customer_comments: average(totalCustomerComments),
    avg_internal_comments: average(totalInternalComments),
    avg_worklogs: average(totalWorklogs),
    avg_time_spent: average(totalLoggedSeconds),

    // Lifecycle metrics are canonical when the runtime has promoted its current
    // status/activity classifications into case.extensions.lifecycle.
    avg_cycle_time_started: lifecycle.avgCycleStartedSeconds,
    avg_resolution_time: lifecycle.avgResolutionSeconds,
    avg_touch_time: lifecycle.avgTouchSeconds,
    avg_wait_time: lifecycle.avgWaitSeconds,
    avg_admin_time: lifecycle.avgAdminSeconds,
    avg_queue_time: lifecycle.avgQueueSeconds,
    avg_flow_efficiency: lifecycle.flowEfficiency,
    workflow_alignment: conformance.workflowAlignment,
    compliance: conformance.compliance,

    metricProvenance: {
      source: 'canonical-process',
      schemaVersion: dataset?.schemaVersion || '',
      directCanonical: [
        'work_items', 'events', 'variants', 'activities', 'steps', 'resources',
        'cycle_time', 'median_cycle_time', 'std_deviation', 'avg_transitions',
        'rework_rate', 'avg_customer_comments', 'avg_internal_comments',
        'avg_worklogs', 'avg_time_spent',
        ...(conformance.workflowAlignment !== null ? ['workflow_alignment'] : []),
        ...(conformance.compliance !== null ? ['compliance'] : []),
        ...(lifecycle.available ? [
          'avg_cycle_time_started', 'avg_resolution_time', 'avg_touch_time',
          'avg_wait_time', 'avg_admin_time', 'avg_queue_time',
          'avg_flow_efficiency',
        ] : []),
      ],
      pendingCanonicalMigration: [
        ...(!lifecycle.available ? [
          'avg_cycle_time_started', 'avg_resolution_time', 'avg_touch_time',
          'avg_wait_time', 'avg_admin_time', 'avg_queue_time',
          'avg_flow_efficiency',
        ] : []),
        ...(conformance.workflowAlignment === null ? ['workflow_alignment'] : []),
        ...(conformance.compliance === null ? ['compliance'] : []),
      ],
      conformance: {
        workflowEvaluatedCount: conformance.workflowEvaluatedCount,
        complianceEvaluatedCount: conformance.complianceEvaluatedCount,
        evaluationSource: conformance.evaluationSource,
      },
      lifecycle: {
        available: lifecycle.available,
        lifecycleCaseCount: lifecycle.lifecycleCaseCount,
        derivation: dataset?.metadata?.lifecycle?.derivation || null,
      },
    },
  };
}
